import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { ROUTES } from '@/config/constants';
import { useSettingsDraft } from './hooks/useSettingsDraft';
import { SettingsSidebar } from './components/SettingsSidebar';
import { SaveBar } from './components/SaveBar';
import { AccountTab } from './components/tabs/AccountTab';
import { NotificationsTab } from './components/tabs/NotificationsTab';
import { PrivacyTab } from './components/tabs/PrivacyTab';
import { ConnectionsTab } from './components/tabs/ConnectionsTab';
import { CommunitiesTab } from './components/tabs/CommunitiesTab';

/**
 * Settings (avatar menu → "Settings"). A clean port of the OG `_v16_settings.js`
 * tabbed surface — Profile, Account, Notifications, Privacy, Connections,
 * Communities — with the sliding-rail sidebar and the floating
 * "unsaved changes" save bar. Draft/dirty/save logic lives in `useSettingsDraft`.
 */
export function SettingsPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { draft, set, dirty, saving, activeTab, setActiveTab, save, discard } = useSettingsDraft();

  async function onSignOut() {
    await signOut();
    navigate(ROUTES.home);
  }

  function renderTab() {
    switch (activeTab) {
      case 'account':
        return <AccountTab draft={draft} set={set} />;
      case 'notifications':
        return <NotificationsTab draft={draft} set={set} />;
      case 'privacy':
        return <PrivacyTab draft={draft} set={set} />;
      case 'connections':
        return <ConnectionsTab />;
      case 'communities':
        return <CommunitiesTab />;
    }
  }

  return (
    <Container className="min-h-screen max-w-none px-8 pb-40 pt-2">
      <header className="mb-6">
        <h1 className="text-[32px] font-black tracking-tight text-hi">Settings</h1>
      </header>

      <div className="grid gap-8 md:grid-cols-[240px_1fr]">
        <SettingsSidebar
          activeTab={activeTab}
          onSelect={setActiveTab}
          signedIn={Boolean(user)}
          onSignOut={onSignOut}
        />
        {/* `key` re-triggers the per-tab fade-in (OG v23 entrance). */}
        <main key={activeTab} className="min-w-0 animate-fade-in-up">
          {renderTab()}
        </main>
      </div>

      <SaveBar show={dirty} saving={saving} onDiscard={discard} onSave={save} />
    </Container>
  );
}
